/**
 * AdoraCare -- the tappable odontogram (TOOTH-NOTATION-INTERIM-V1), upgraded
 * to the PROFESSIONAL ANATOMICAL 2D chart by DOCTOR-OS-EXPERIENCE-V1
 * section D (honest-delivery ruling D10: the owned anatomical 2D/SVG path).
 *
 * Founder rulings R1-R3 (2026-07-30): a recognizable tooth grid the doctor
 * TAPS is the primary September entry; every tooth shows its org-default
 * notation token (FDI seeded) and its canonical name on hover/assistive
 * text; the ADULT dentition renders first with the primary (deciduous) view
 * behind an explicit toggle; a typed FDI/Universal entry is offered as the
 * ASSISTIVE-SECONDARY method only -- it resolves against the served set and
 * an invalid entry can never join the selection.
 *
 * SECTION D UPGRADE (presentation only -- the tap engine, testids, props,
 * and payloads are byte-compatible): each tooth button now draws a small
 * anatomical occlusal/incisal-view SVG crown glyph ABOVE its still-visible
 * notation token, the arch rows curve (upper bows downward -- the smile
 * arc -- lower mirrors upward, midline gap kept), and an OPTIONAL per-tooth
 * `overlays` prop renders condition / treatment / evidence / assignment
 * marks plus a compact legend. All glyphs are HAND-AUTHORED OWNED SVG
 * GLYPHS by default. The approved C option uses the supplied data-only
 * artwork inside the same controls; no prototype code is executed.
 *
 * CLINICAL SAFETY: selection is a FACT-capture control -- it infers nothing,
 * recommends nothing, and translates nothing silently (display tokens come
 * from the served mappings). The org display choice changes RENDER only;
 * the canonical code is what the form carries and the server stores. The
 * overlays prop is RENDER-ONLY caller-held fact -- it never widens a read.
 *
 * A controlled component: `selected` (canonical codes) + `onChange`. The
 * caller decides persistence (autosave patch / item payload). Reused at BOTH
 * grains (diagnosis section + plan-item forms) -- one component, no fork.
 */

import { useState, type CSSProperties } from 'react';

import type { ToothNotationDisplayView, ToothReferenceView } from '../api';
import {
  archToothPlacement,
  isQuadrantSelected,
  quadrantActionLabel,
  resolveTypedTooth,
  selectedToothRefs,
  toggleQuadrantCodes,
  toggleToothCode,
  toothDiagramArches,
  toothDisplayLabel,
  toothDisplayToken,
  toothOverlayLabel,
  toothOverlayLegend,
  toothQuadrantGroups,
  TOOTH_STORAGE_UNAVAILABLE_HINT,
  type ToothDentitionView,
  type ToothOverlayLegendKey,
  type ToothOverlayView,
} from './tooth-diagram-view';
import {
  anatomicalToothArt,
  TOOTH_TILE_H,
  TOOTH_TILE_W,
} from './tooth-anatomy';
import overviewArt from './clinical-overview-arch-assets.json';
import './tooth-diagram.css';
import { AcButton } from '@/dashboard/ui';

/**
 * DOCTOR-COCKPIT-VISUAL-CONFORM-V1 -- the ANATOMICAL presentation swap
 * (founder-explicit ODONTOGRAM STANDARD). The schematic occlusal glyph
 * tiles are replaced by the professional buccal-view anatomical teeth
 * (crown + root(s) + gingival collar) from the banked DENTAL-CHART brief:
 * per-position geometry (s.25) via tooth-anatomy.ts, the s.24 layer order
 * (gingiva -> roots -> tissue veil -> crown -> detail -> glaze -> outline)
 * as stacked paths over shared gradients, the s.23 --dc-anat-* palette
 * (registered in tooth-diagram.css BESIDE the --ac-* set -- the one
 * documented anatomy exception), and the s.26 state-overlay rules (glazes
 * never erase anatomy; selection stays the brand ring, never a fill).
 * Gradient-only shading -- zero SVG filters, zero images, zero libraries
 * (the NO-3D FENCE). The tap engine, testids, props and payloads are
 * byte-compatible (the D-5 presentation-swap contract).
 */

/** Vertical mirror for the lower arch (same owned paths, flipped). */
const ART_MIRROR_LOWER = `translate(0 ${TOOTH_TILE_H}) scale(1 -1)`;

/** The shared per-mount gradient definitions (brief s.24: stacked paths
 *  over shared objectBoundingBox gradients). One hidden svg per diagram
 *  mount; ids are idPrefix-scoped so the two grains on one page never
 *  collide. stop colors resolve the --dc-anat-* custom properties. */
function AnatomyPaintDefs({ idPrefix }: { idPrefix: string }): JSX.Element {
  return (
    <svg
      className="tooth-diagram__paint-defs"
      aria-hidden="true"
      focusable="false"
      width="0"
      height="0"
    >
      <defs>
        {/* Root dentin: darker at the apex (top in authored orientation). */}
        <linearGradient id={`${idPrefix}-anat-root`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" style={{ stopColor: 'hsl(var(--dc-anat-dentin-deep))' }} />
          <stop offset="0.7" style={{ stopColor: 'hsl(var(--dc-anat-dentin))' }} />
        </linearGradient>
        {/* Crown body: cervical dentin-deep -> mid dentin -> working-edge
            enamel (the s.24 layer-4 body gradient). */}
        <linearGradient id={`${idPrefix}-anat-crown`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" style={{ stopColor: 'hsl(var(--dc-anat-dentin-deep))' }} />
          <stop offset="0.34" style={{ stopColor: 'hsl(var(--dc-anat-dentin))' }} />
          <stop offset="0.9" style={{ stopColor: 'hsl(var(--dc-anat-enamel))' }} />
        </linearGradient>
        {/* Incisal/occlusal edge translucency (layer 5). */}
        <linearGradient id={`${idPrefix}-anat-edge`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0.55" style={{ stopColor: 'hsl(var(--dc-anat-enamel-cool))', stopOpacity: 0 }} />
          <stop offset="1" style={{ stopColor: 'hsl(var(--dc-anat-enamel-cool))', stopOpacity: 0.6 }} />
        </linearGradient>
        {/* Cervical shadow band over the neck fifth (layer 6). */}
        <linearGradient id={`${idPrefix}-anat-cervical`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" style={{ stopColor: 'hsl(var(--dc-anat-cervical))', stopOpacity: 0.5 }} />
          <stop offset="0.24" style={{ stopColor: 'hsl(var(--dc-anat-cervical))', stopOpacity: 0 }} />
        </linearGradient>
        {/* Gingiva: attached-tissue highlight toward the margin edge
            (layers 1 + 3; the veil rides the fill opacity in CSS). */}
        <linearGradient id={`${idPrefix}-anat-gum`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" style={{ stopColor: 'hsl(var(--dc-anat-gum-light))' }} />
          <stop offset="0.62" style={{ stopColor: 'hsl(var(--dc-anat-gum))' }} />
          <stop offset="1" style={{ stopColor: 'hsl(var(--dc-anat-gum-deep))' }} />
        </linearGradient>
      </defs>
    </svg>
  );
}

/** One anatomical tooth tile + its overlay marks. The art is authored in
 *  UPPER orientation (root apex up); the lower arch mirrors the anatomy
 *  group only. Overlay marks draw in UNMIRRORED tile coordinates so their
 *  corners stay stable on both arches: treatment badge top-right, evidence
 *  clip top-left, assigned-elsewhere ring bottom-right, strike across. */
function AnatomicalTooth({
  toothCode,
  dentition,
  arch,
  idPrefix,
  overlay,
  overview = false,
}: {
  toothCode: string;
  dentition: ToothDentitionView;
  arch: 'upper' | 'lower';
  idPrefix: string;
  overlay: ToothOverlayView | undefined;
  overview?: boolean;
}): JSX.Element {
  const art = anatomicalToothArt(toothCode, dentition, arch);
  // Art keys are never persisted or exposed as tooth identity. The selected
  // prototype mirrors the patient's left-side artwork; primary teeth keep
  // their existing dedicated SVG geometry rather than borrowing adult art.
  const fdi = Number(toothCode);
  const artKey = fdi >= 21 && fdi <= 28 ? `${fdi - 10}m`
    : fdi >= 31 && fdi <= 38 ? `${fdi + 10}m` : toothCode;
  const raster = overview && dentition === 'permanent'
    ? (overviewArt.assets as Record<string, { d: string }>)[artKey] : undefined;
  const condition = overlay?.condition;
  const glaze =
    overlay?.treatment === 'proposed'
      ? 'proposed'
      : overlay?.treatment === 'accepted'
        ? 'accepted'
        : overlay?.treatment === 'completed'
          ? 'completed'
          : null;
  return (
    <svg
      className={`tooth-diagram__anat${
        dentition === 'primary' ? ' tooth-diagram__anat--primary' : ''
      }`}
      viewBox={`0 0 ${TOOTH_TILE_W} ${TOOTH_TILE_H}`}
      aria-hidden="true"
      focusable="false"
    >
      {raster ? (
        <image href={raster.d} width={TOOTH_TILE_W} height={TOOTH_TILE_H}
          preserveAspectRatio="xMidYMid meet" data-artwork="approved-c" />
      ) : <g transform={arch === 'lower' ? ART_MIRROR_LOWER : undefined}>
        {/* 1. gingiva body + margin scallop (doubles as the tissue band). */}
        <path
          d={art.gumline}
          fill={`url(#${idPrefix}-anat-gum)`}
          className="tooth-anat__gum"
        />
        {/* 2. roots (ghosted palatal/far root behind, at reduced opacity). */}
        {art.roots.map((d, i) => (
          <path
            key={`r${i}`}
            d={d}
            fill={`url(#${idPrefix}-anat-root)`}
            className={
              art.ghostFirstRoot && i === 0
                ? 'tooth-anat__root tooth-anat__root--ghost'
                : 'tooth-anat__root'
            }
          />
        ))}
        {/* 3. tissue veil OVER the roots: the gum band re-painted at veil
            opacity so roots read as "inside the gum" (s.24 layer 3). */}
        <path
          d={art.gumline}
          fill={`url(#${idPrefix}-anat-gum)`}
          className="tooth-anat__veil"
        />
        {/* 4. crown base: per-position silhouette, dentin body gradient. */}
        <path
          d={art.crown}
          fill={`url(#${idPrefix}-anat-crown)`}
          className="tooth-anat__crown"
        />
        {/* 5. edge translucency + 6. cervical shadow (gradient overlays). */}
        <path d={art.crown} fill={`url(#${idPrefix}-anat-edge)`} />
        <path d={art.crown} fill={`url(#${idPrefix}-anat-cervical)`} />
        {/* 7a. developmental grooves (cervical tone, 1px). */}
        {art.grooves !== '' ? (
          <path d={art.grooves} className="tooth-anat__groove" />
        ) : null}
        {/* 7b. state glaze (s.26: a readable tint OVER the anatomy --
            never erases it; alpha inside the .22-.32 floor). */}
        {glaze !== null ? (
          <path
            d={art.crown}
            className={`tooth-anat__glaze tooth-anat__glaze--${glaze}`}
          />
        ) : null}
        {/* 7c. the soft anatomical outline; selection/hover/state recolor
            THIS stroke only (s.26: selection is a ring, never a fill). */}
        <path d={art.crown} className="tooth-anat__halo" />
        <path d={art.crown} className="tooth-anat__outline" />
      </g>}
      {raster && condition === 'unerupted' ? (
        <rect x="2" y="2" width={TOOTH_TILE_W - 4} height={TOOTH_TILE_H - 4}
          rx="4" className="tooth-diagram__raster-unerupted" />
      ) : null}
      {overlay?.treatment !== undefined ? (
        <circle
          cx={TOOTH_TILE_W - 5}
          cy="6"
          r="3.4"
          className={`tooth-diagram__glyph-badge tooth-diagram__glyph-badge--${overlay.treatment}`}
        />
      ) : null}
      {overlay?.evidence === true ? (
        <g className="tooth-diagram__glyph-evidence">
          <rect
            x="3"
            y="3"
            width="5.4"
            height="6.6"
            rx="1.5"
            className="tooth-diagram__glyph-evidence-body"
          />
          <path d="M 5.7 4.8 L 5.7 7.8" className="tooth-diagram__glyph-evidence-wire" />
        </g>
      ) : null}
      {overlay?.assignedElsewhere === true ? (
        <circle
          cx={TOOTH_TILE_W - 5}
          cy={TOOTH_TILE_H - 6}
          r="3"
          className="tooth-diagram__glyph-ring"
        />
      ) : null}
      {condition === 'missing' || condition === 'extracted' ? (
        <line
          x1="6"
          y1={TOOTH_TILE_H - 10}
          x2={TOOTH_TILE_W - 6}
          y2="10"
          className={`tooth-diagram__glyph-strike tooth-diagram__glyph-strike--${condition}`}
        />
      ) : null}
    </svg>
  );
}

/** Legend swatch for one overlay state -- the SAME mark vocabulary the
 *  glyphs draw (shared classes, so the tones can never drift apart). */
function LegendMarker({ marker }: { marker: ToothOverlayLegendKey }): JSX.Element {
  if (marker === 'missing' || marker === 'extracted') {
    return (
      <svg
        className="tooth-diagram__legend-marker"
        viewBox="0 0 14 14"
        aria-hidden="true"
        focusable="false"
      >
        <rect x="2" y="3.5" width="10" height="7" rx="2.4" className="tooth-diagram__legend-swatch" />
        <line
          x1="2.4"
          y1="11.6"
          x2="11.6"
          y2="2.4"
          className={`tooth-diagram__glyph-strike tooth-diagram__glyph-strike--${marker}`}
        />
      </svg>
    );
  }
  if (marker === 'unerupted') {
    return (
      <svg
        className="tooth-diagram__legend-marker"
        viewBox="0 0 14 14"
        aria-hidden="true"
        focusable="false"
      >
        <rect
          x="2"
          y="3.5"
          width="10"
          height="7"
          rx="2.4"
          className="tooth-diagram__legend-swatch tooth-diagram__legend-swatch--dashed"
        />
      </svg>
    );
  }
  if (marker === 'evidence') {
    return (
      <svg
        className="tooth-diagram__legend-marker"
        viewBox="0 0 14 14"
        aria-hidden="true"
        focusable="false"
      >
        <g className="tooth-diagram__glyph-evidence">
          <rect
            x="4.5"
            y="3.5"
            width="5"
            height="6"
            rx="1.4"
            className="tooth-diagram__glyph-evidence-body"
          />
          <path d="M 7 5.1 L 7 7.9" className="tooth-diagram__glyph-evidence-wire" />
        </g>
      </svg>
    );
  }
  if (marker === 'assigned-elsewhere') {
    return (
      <svg
        className="tooth-diagram__legend-marker"
        viewBox="0 0 14 14"
        aria-hidden="true"
        focusable="false"
      >
        <circle cx="7" cy="7" r="3.2" className="tooth-diagram__glyph-ring" />
      </svg>
    );
  }
  // The four treatment states share the badge dot.
  return (
    <svg
      className="tooth-diagram__legend-marker"
      viewBox="0 0 14 14"
      aria-hidden="true"
      focusable="false"
    >
      <circle
        cx="7"
        cy="7"
        r="3.4"
        className={`tooth-diagram__glyph-badge tooth-diagram__glyph-badge--${marker}`}
      />
    </svg>
  );
}

export function ToothDiagram({
  references,
  display,
  selected,
  disabled,
  storageLive = true,
  idPrefix,
  title,
  onChange,
  initialDentition = 'permanent',
  overlays,
  artwork = 'anatomical',
  layout = 'existing',
  selectionMode = 'multiple',
  foundToday = [],
  earlierRecords = [],
}: {
  /** The served canonical reference set (consult-context wire). */
  references: readonly ToothReferenceView[];
  /** The org display default ('fdi' seeded; ruling R1/R4). */
  display: ToothNotationDisplayView;
  /** Selected canonical tooth codes (order = selection order). */
  selected: readonly string[];
  disabled: boolean;
  /**
   * Whether a selection can be SAVED (DOCTOR-PRECALL-POLISH-V1, the
   * diagram-render fix). false = the chart still RENDERS -- the doctor sees
   * the real arches -- but every control is LOCKED and the honest line says
   * why, so no unsavable selection can ever be built. Default true keeps
   * every existing caller byte-identical.
   */
  storageLive?: boolean;
  /** Unique per mount (two grains share one page) -- drives testids/ids. */
  idPrefix: string;
  title: string;
  onChange: (next: string[]) => void;
  /** ADULT-FIRST default (ruling R3); overridable for static render tests. */
  initialDentition?: ToothDentitionView;
  /**
   * OPTIONAL per-tooth overlay states keyed by canonical code (section D):
   * condition (missing / extracted / unerupted), treatment badge, evidence
   * mark, assigned-elsewhere ring -- plus the compact legend row when any
   * state is present. RENDER-ONLY caller-held facts; absent = the plain
   * chart, so every existing caller compiles and renders unchanged.
   */
  overlays?: Readonly<Record<string, ToothOverlayView>>;
  /** Approved C presentation; all selection, overlay and storage semantics stay shared. */
  artwork?: 'anatomical' | 'clinical-overview';
  layout?: 'existing' | 'v14g';
  selectionMode?: 'single' | 'multiple';
  foundToday?: readonly string[];
  /** Admitted read-only history only; separate from current findings/plans. */
  earlierRecords?: readonly string[];
}): JSX.Element {
  // ADULT-FIRST (ruling R3): permanent renders by default; the primary
  // dentition sits behind this explicit, labeled toggle. Selection is
  // preserved across the toggle (chips always show every selected tooth).
  const [dentition, setDentition] = useState<ToothDentitionView>(initialDentition);
  const [typed, setTyped] = useState('');
  const [typedError, setTypedError] = useState<string | null>(null);

  // THE ONE control gate: a busy page or a non-live tooth store both lock the
  // chart. Every control below reads `locked`, so a locked chart can never
  // emit an onChange -- the item/draft payloads stay byte-identical to the
  // no-selection shape and no write can 409 (the write fence, kept).
  const locked = disabled || !storageLive;

  const arches = toothDiagramArches(
    references.filter((t) => t.dentition === dentition),
    dentition,
  );
  const chips = selectedToothRefs(references, selected);
  // Legend entries derive from the overlays PROP (the chart's whole overlay
  // vocabulary), so the row names every state the caller supplied.
  const legend = toothOverlayLegend(overlays);

  const addTyped = (): void => {
    if (locked) return;
    const match = resolveTypedTooth(references, typed);
    if (match === null) {
      setTypedError(
        'Not a valid tooth code. Enter an FDI number (e.g. 36) or a Universal token (e.g. 19 or A).',
      );
      return;
    }
    setTypedError(null);
    setTyped('');
    if (!selected.includes(match.toothCode)) {
      onChange([...selected, match.toothCode]);
    }
  };

  if (layout === 'v14g') {
    const adult = toothDiagramArches(references.filter(t => t.dentition === 'permanent'), 'permanent');
    const milk = toothDiagramArches(references.filter(t => t.dentition === 'primary'), 'primary');
    const row = (teeth: readonly ToothReferenceView[], arch: 'upper' | 'lower', primary = false) =>
      <div className={`consult-v14g__tooth-row${primary ? ' consult-v14g__tooth-row--primary' : ''}`}>
        {teeth.map(tooth => {
          const overlay = overlays?.[tooth.toothCode];
          return <AcButton key={tooth.toothCode} variant="ghost"
            className={`consult-v14g__tooth${foundToday.includes(tooth.toothCode) ? ' is-found' : ''}${overlay?.treatment ? ` is-${overlay.treatment}` : ''}${earlierRecords.includes(tooth.toothCode) ? ' has-earlier-records' : ''}`}
            disabled={locked} aria-pressed={selected.includes(tooth.toothCode)}
            aria-label={`${toothDisplayLabel(tooth, display)}${earlierRecords.includes(tooth.toothCode) ? ', has earlier records' : ''}`} title={toothDisplayLabel(tooth, display)}
            data-testid={`${idPrefix}-tooth-${tooth.toothCode}`}
            onClick={() => onChange(selectionMode === 'single'
              ? selected.length === 1 && selected[0] === tooth.toothCode ? [] : [tooth.toothCode]
              : toggleToothCode(selected, tooth.toothCode))}>
            <AnatomicalTooth toothCode={tooth.toothCode} dentition={tooth.dentition} arch={arch}
              idPrefix={idPrefix} overlay={overlay} overview={artwork === 'clinical-overview'} />
            <span>{toothDisplayToken(tooth, display)}</span>
          </AcButton>;
        })}
      </div>;
    return <div className="consult-v14g__chart" data-testid={`${idPrefix}-diagram`} aria-label={title}>
      <AnatomyPaintDefs idPrefix={idPrefix} />
      <div className="consult-v14g__mouth">
      <div className="consult-v14g__orientation"><span>Upper right</span><span>Upper left</span></div>
      {row(adult[0]!.teeth, 'upper')}
      {dentition === 'primary' ? row(milk[0]!.teeth, 'upper', true) : null}
      <div className="consult-v14g__midline" />
      {dentition === 'primary' ? row(milk[1]!.teeth, 'lower', true) : null}
      {row(adult[1]!.teeth, 'lower')}
      <div className="consult-v14g__orientation"><span>Lower right</span><span>Lower left</span></div>
      </div>
      <div className="consult-v14g__legend">
        <span><i className="found" />Found today</span><span><i className="accepted" />Planned</span><span><i className="advised" />Advised</span><span><i className="history" aria-hidden="true">H</i>Earlier records</span>
        <AcButton variant="ghost" aria-pressed={dentition === 'primary'} data-testid={`${idPrefix}-dentition-toggle`}
          onClick={() => setDentition(dentition === 'primary' ? 'permanent' : 'primary')}>{dentition === 'primary' ? 'Hide milk teeth' : 'Show milk teeth'}</AcButton>
      </div>
      {!storageLive ? <p role="status">{TOOTH_STORAGE_UNAVAILABLE_HINT}</p> : null}
    </div>;
  }

  return (
    <div className={`tooth-diagram${artwork === 'clinical-overview' ? ' tooth-diagram--overview' : ''}`} data-testid={`${idPrefix}-diagram`}>
      <AnatomyPaintDefs idPrefix={idPrefix} />
      <div className="tooth-diagram__head">
        <span className="tooth-diagram__title">{title}</span>
        <button
          type="button"
          className="tooth-diagram__chip"
          disabled={locked}
          aria-pressed={dentition === 'primary'}
          data-testid={`${idPrefix}-dentition-toggle`}
          onClick={() =>
            setDentition(dentition === 'primary' ? 'permanent' : 'primary')
          }
        >
          {dentition === 'primary'
            ? 'Showing primary teeth - switch to adult'
            : 'Show primary teeth'}
        </button>
      </div>

      {/* THE HONEST DEGRADED LINE (DOCTOR-PRECALL-POLISH-V1): storage not
          live -> the chart still draws, and ONE muted line names why it is
          read-only. A doctor can now tell "this environment is behind" from
          "this product has no tooth chart" -- the blank could not. */}
      {!storageLive ? (
        <span
          className="tooth-diagram__empty"
          data-testid={`${idPrefix}-storage-unavailable`}
        >
          {TOOTH_STORAGE_UNAVAILABLE_HINT}
        </span>
      ) : null}

      {/* V2 quadrant composition (DOCTOR-OS-BUILD-PHASE-1-V1, mockup 04 D1):
          each arch renders as its TWO labeled FDI quadrant groups -- names
          from the ISO 3950 standard digits only, never incumbent imagery
          (E6). Section D curves the rows: per-tooth placement (rotate +
          translateY from archToothPlacement) rides CSS custom properties so
          the stylesheet can flatten the curve on narrow viewports. Same tap
          engine, same testids, same order; presentation only. */}
      <div className="tooth-diagram__arches">
        {arches.map((arch) => {
          // The curve spans the WHOLE viewer-order arch row (both quadrant
          // groups), so index each tooth against the full row once.
          const archIndex = new Map(arch.teeth.map((t, i) => [t.toothCode, i]));
          return (
            <div
              key={arch.arch}
              className={`tooth-diagram__arch tooth-diagram__arch--${arch.arch}`}
              data-testid={`${idPrefix}-arch-${arch.arch}`}
            >
              <span className="tooth-diagram__arch-label">{arch.arch}</span>
              {toothQuadrantGroups(arch.teeth).map((quadrant, qIndex) => {
                // DOCTOR-QUADRANT-SELECT-V1: the group label that ALREADY
                // rendered here becomes THE control -- no new chart, no new
                // component, no redraw (the case-sheet anatomy authority is
                // Cockpit and founder confirmation of that reconcile is
                // PENDING). Pressed = every tooth of the quadrant is already
                // selected, so the next tap clears exactly those; a partial
                // quadrant completes instead of clearing. The value handed to
                // onChange is byte-identical to the one N per-tooth taps
                // produce (toggleQuadrantCodes; proven in the suite).
                const quadrantSelected = isQuadrantSelected(selected, quadrant.teeth);
                const quadrantLabel = quadrantActionLabel(quadrant, quadrantSelected);
                return (
                  <span
                    key={quadrant.digit}
                    className="tooth-diagram__quadrant"
                    data-testid={`${idPrefix}-quadrant-${quadrant.digit}`}
                  >
                    <button
                      type="button"
                      className="tooth-diagram__chip tooth-diagram__quadrant-label"
                      disabled={locked}
                      aria-pressed={quadrantSelected}
                      aria-label={quadrantLabel}
                      title={quadrantLabel}
                      data-testid={`${idPrefix}-quadrant-select-${quadrant.digit}`}
                      onClick={() =>
                        onChange(toggleQuadrantCodes(selected, quadrant.teeth))
                      }
                    >
                      {quadrant.label}
                    </button>
                    <span className="tooth-diagram__quadrant-teeth">
                      {quadrant.teeth.map((tooth, index) => {
                        const overlay =
                          overlays === undefined
                            ? undefined
                            : overlays[tooth.toothCode];
                        const overlayText = toothOverlayLabel(overlay);
                        // Screen-reader parity (D5): overlay state words join
                        // the one label used for BOTH aria-label and title.
                        const label =
                          overlayText === null
                            ? toothDisplayLabel(tooth, display)
                            : `${toothDisplayLabel(tooth, display)} - ${overlayText}`;
                        const placement = archToothPlacement(
                          archIndex.get(tooth.toothCode) ?? 0,
                          arch.teeth.length,
                          arch.arch,
                        );
                        const classNames = ['tooth-diagram__tooth'];
                        if (qIndex > 0 && index === 0) {
                          classNames.push('tooth-diagram__tooth--midline');
                        }
                        if (overlay?.condition !== undefined) {
                          classNames.push(`tooth-diagram__tooth--${overlay.condition}`);
                        }
                        return (
                          <button
                            key={tooth.toothCode}
                            type="button"
                            className={classNames.join(' ')}
                            style={
                              {
                                '--tooth-dy': `${placement.offsetY}px`,
                                '--tooth-rot': `${placement.rotateDeg}deg`,
                              } as CSSProperties
                            }
                            disabled={locked}
                            aria-pressed={selected.includes(tooth.toothCode)}
                            aria-label={label}
                            title={label}
                            data-testid={`${idPrefix}-tooth-${tooth.toothCode}`}
                            onClick={() =>
                              onChange(toggleToothCode(selected, tooth.toothCode))
                            }
                          >
                            <AnatomicalTooth
                              toothCode={tooth.toothCode}
                              dentition={tooth.dentition}
                              arch={arch.arch}
                              idPrefix={idPrefix}
                              overlay={overlay}
                              overview={artwork === 'clinical-overview'}
                            />
                            <span className="tooth-diagram__tooth-token">
                              {toothDisplayToken(tooth, display)}
                            </span>
                          </button>
                        );
                      })}
                    </span>
                  </span>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Compact overlay legend (section D): renders ONLY when the caller
          supplied at least one overlay state -- labels with their markers,
          never sentences. */}
      {legend.length > 0 ? (
        <ul className="tooth-diagram__legend" data-testid={`${idPrefix}-legend`}>
          {legend.map((entry) => (
            <li key={entry.key} className="tooth-diagram__legend-item">
              <LegendMarker marker={entry.key} />
              <span>{entry.label}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {chips.length > 0 ? (
        <ul className="tooth-diagram__chips" data-testid={`${idPrefix}-selected`}>
          {chips.map((tooth) => (
            <li key={tooth.toothCode}>
              <button
                type="button"
                className="tooth-diagram__chip"
                disabled={locked}
                aria-label={`Remove ${toothDisplayLabel(tooth, display)}`}
                data-testid={`${idPrefix}-selected-${tooth.toothCode}`}
                onClick={() => onChange(toggleToothCode(selected, tooth.toothCode))}
              >
                {toothDisplayLabel(tooth, display)}
                <span aria-hidden="true" className="tooth-diagram__chip-x">
                  x
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <span className="tooth-diagram__empty">No teeth selected.</span>
      )}

      {/* Assistive-secondary typed entry (ruling R2: never the primary). */}
      <div className="tooth-diagram__assist">
        <input
          type="text"
          className="clinic-workbench__input tooth-diagram__assist-input"
          placeholder="Type a code, press Enter"
          aria-label="Add a tooth by typed code (assistive)"
          value={typed}
          disabled={locked}
          data-testid={`${idPrefix}-tooth-input`}
          onChange={(e) => {
            setTyped(e.target.value);
            if (typedError !== null) setTypedError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addTyped();
            }
          }}
        />
        {typedError !== null ? (
          <span
            className="tooth-diagram__error"
            role="status"
            aria-live="polite"
            data-testid={`${idPrefix}-tooth-input-error`}
          >
            {typedError}
          </span>
        ) : null}
      </div>
    </div>
  );
}
